import { Activity, Camera, CircleAlert, Signal, UserRound } from 'lucide-react';
import { Observation } from '../types/domain';
import { PresenceCapability, PresenceState } from '../utils/candidatePresence';

interface MonitoringStatusProps {
  active: Observation[];
  presenceCapability: PresenceCapability;
  presenceState: PresenceState;
  mobileConnected: boolean;
  mobileRequired: boolean;
}

export function MonitoringStatus({ active, presenceCapability, presenceState, mobileConnected, mobileRequired }: MonitoringStatusProps) {
  const attention = (source: Observation['source']) => active.some((item) => item.source === source);
  const laptopCameraAttention = active.some((item) => item.source === 'laptop-camera' && item.type !== 'candidate-absent');
  const presenceStatus = presenceCapability === 'unavailable' || presenceCapability === 'degraded'
    ? 'Temporarily unavailable'
    : presenceState === 'warming-up' || presenceCapability === 'checking'
      ? 'Starting'
      : presenceState === 'absent-confirmed' || presenceState === 'recovering'
      ? 'Candidate temporarily absent'
      : 'Active';
  const rows = [
    { label: 'Laptop camera', icon: Camera, state: laptopCameraAttention ? 'Attention needed' : 'Healthy' },
    { label: 'Secondary camera', icon: Camera, state: mobileConnected ? (attention('mobile-camera') ? 'Attention needed' : 'Monitoring') : mobileRequired ? 'Unavailable' : 'Optional / not connected' },
    { label: 'Candidate presence', icon: UserRound, state: presenceStatus },
    { label: 'Connection', icon: Signal, state: attention('network') ? 'Recovering' : mobileConnected ? 'Ready' : mobileRequired ? 'Unavailable' : 'Optional' },
    { label: 'Monitoring status', icon: Activity, state: active.length ? 'Attention needed' : 'Ready' }
  ];

  return (
    <section aria-label="Monitoring status" className="rounded-md border border-white/10 bg-white/[0.05] p-3">
      <h2 className="flex items-center gap-2 text-sm font-semibold text-white"><Activity className="h-4 w-4 text-neon-blue" />Monitoring</h2>
      <dl className="mt-3 space-y-2 text-xs">
        {rows.map(({ label, icon: Icon, state }) => {
          const needsAttention = state === 'Attention needed' || state === 'Unavailable' || state === 'Recovering' || state === 'Temporarily unavailable' || state === 'Candidate temporarily absent';
          return (
            <div key={label} className="flex items-center justify-between gap-3">
              <dt className="flex items-center gap-2 text-slate-300"><Icon className="h-3.5 w-3.5" />{label}</dt>
              <dd className={`text-right font-semibold ${needsAttention ? 'text-amber-200' : 'text-mint'}`}>{needsAttention ? <CircleAlert className="mr-1 inline h-3.5 w-3.5" /> : null}{state}</dd>
            </div>
          );
        })}
      </dl>
    </section>
  );
}
